<template>
  <div>
    <TextField
      :id="id"
      :model-value="modelValue"
      :icon="EmailIcon"
      :is-marked-as-invalid="errors.length > 0"
      :errors="errors"
      :class="emailClassName"
      name="email"
      type="email"
      class="mb-4"
      :placeholder="placeholder"
      :data-test-id="dataTestId"
      @input="isEmailValid = true; emailAutoCorrect = '';"
      @update:modelValue="$emit('update:modelValue', $event)"
    />
    <div
      v-if="emailAutoCorrect"
      class="-mt-3 mb-3 text-sm"
    >
      Did you mean
      <button
        class="bg-white border-0 p-0 my-1 text-left whitespace-normal break-words w-full"
        data-test-id="correct-email-suggestion"
        @click="$emit('update:modelValue', emailAutoCorrect); emailAutoCorrect = ''; isEmailValid = true;"
      >
        <span class="text-underline">{{ emailAutoCorrect }}</span>?
      </button>
    </div>

    <InvalidEmailWarning
      v-if="!isEmailValid"
      class="my-3 mb-4"
    />
  </div>
</template>

<script>
import EmailIcon from '@tagvenue/icons/24/line/communication/mail-01.svg';
import debounce from 'lodash/debounce';
import { markRaw } from 'vue';

import { mailCheck } from '@/services/mailcheck';

import TextField from '../Ui/Forms/TextField.vue';
import htmlProps from '../Ui/htmlProps.js';
import InvalidEmailWarning from './InvalidEmailWarning.vue';

export default {
  name: 'EmailField',
  emits: ['update:modelValue'],

  mixins: [htmlProps],
  components: { InvalidEmailWarning, TextField },

  props: {
    id: {
      type: String,
      default: null,
    },
    modelValue: {
      type: String,
      required: true,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Email - Watch out for typos',
    },
    invalidStyle: {
      type: String,
      default: 'warning',
      validator: (value) => ['warning', 'error'].includes(value),
    },
    disableValidation: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      EmailIcon: markRaw(EmailIcon),
      isEmailValid: true,
      emailAutoCorrect: '',
      activeRequest: null,
    };
  },

  computed: {
    emailClassName() {
      if (!this.isEmailValid || this.emailAutoCorrect) {
        return this.invalidStyle === 'warning' ? 'email--invalid-warning' : 'email--invalid-error';
      }
      return '';
    },
  },

  watch: {
    modelValue() {
      if (!this.disableValidation) {
        this.validateEmail();
      }
    },
  },

  methods: {
    resetValidation() {
      this.isEmailValid = true;
      this.emailAutoCorrect = '';
    },
    validateEmail: debounce(async function () {
      mailCheck({
        email: this.modelValue,
        onSuggestion: (suggestion) => {
          if (suggestion && suggestion.full && suggestion.domain.indexOf('.') > 0) {
            this.emailAutoCorrect = suggestion.full;
          } else {
            this.emailAutoCorrect = '';
          }
        },
        onEmpty: () => {
          this.emailAutoCorrect = '';
        },
      });

      if (!this.modelValue || !App.es6.shared.regex_validator().isValidEmail(this.modelValue)) {
        return;
      }

      if (this.activeRequest) {
        this.activeRequest.abort();
      }

      this.activeRequest = $.ajax({
        method: 'get',
        dataType: 'json',
        url: '/ajax/validate-email',
        data: {
          email: this.modelValue,
        },
      })
        .done((data) => {
          if (data && data.valid) {
            this.isEmailValid = true;
            return;
          }
          this.isEmailValid = false;
          this.activeRequest = null;
        })
        .fail(() => {
          this.isEmailValid = true;
          this.activeRequest = null;
        });
    }, 1000),
  },
};
</script>

<style lang="scss" scoped>
@use "@sass/design-system/variables" as ds-variables;

.email--invalid-warning :deep(input) {
  border-color: ds-variables.$yellow;
}

.email--invalid-error :deep(input) {
  border-color: ds-variables.$red;
}
</style>
